// Application Configuration
// This file manages the app-wide configuration that can be customized

export interface AppConfig {
  companyName: string;
  portalName: string;
  supportEmail: string;
  timezone: string;
  language: string;
  dateFormat: string;
  logoUrl?: string;
  welcomeMessage?: string;
}

// Default configuration
const defaultConfig: AppConfig = {
  companyName: 'Guston Ltd',
  portalName: 'Guston Trade Portal',
  supportEmail: 'info@gustonltd.com',
  timezone: 'UTC',
  language: 'en',
  dateFormat: 'MM/DD/YYYY',
  logoUrl: '/guston-logo.webp',
  welcomeMessage: 'Register to supply or source with Guston.',
};

// Storage key for localStorage
const CONFIG_STORAGE_KEY = 'app_config';

class AppConfigManager {
  private config: AppConfig;

  constructor() {
    this.config = this.loadConfig();
  }

  /**
   * Load configuration from localStorage or use defaults
   */
  private loadConfig(): AppConfig {
    try {
      const stored = localStorage.getItem(CONFIG_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultConfig, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load app config from localStorage:', error);
    }
    return { ...defaultConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): AppConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<AppConfig>): void {
    this.config = { ...this.config, ...updates };
    this.saveConfig();
  }

  /**
   * Save configuration to localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(this.config));
    } catch (error) {
      console.error('Failed to save app config to localStorage:', error);
    }
  }

  /**
   * Reset configuration to defaults
   */
  resetConfig(): void {
    this.config = { ...defaultConfig };
    this.saveConfig();
  }

  /**
   * Get a specific config value
   */
  get<K extends keyof AppConfig>(key: K): AppConfig[K] {
    return this.config[key];
  }
}

// Export singleton instance
export const appConfig = new AppConfigManager();

// Export default config for reference
export { defaultConfig };
